import { useAreaSelection } from 'hooks/useAreaSelection'
import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react'

export const SelectionContext = createContext({})

export const useSelectionContext = () => useContext(SelectionContext)

/**
 * Selection Context Provider - handles area selection for deck panels and sideliners
 *
 * @param {object} props
 * @param {React.RefObject} props.selectContainerRef - Reference to the container element for selection
 * @param {Function} props.onPanelClick - Callback when panels are selected
 * @param {boolean} props.isEnabled - Whether selection is enabled
 * @param {React.MutableRefObject<boolean>} props.wasMultiSelection - Ref tracking if last selection was multi-select
 * @param {React.ReactNode} props.children - Child components
 */
export const SelectionContextProvider = ({
  selectContainerRef,
  onPanelClick,
  isEnabled,
  wasMultiSelection,
  children
}) => {
  const { selection, setSelection, mouseDown } = useAreaSelection({
    containerRef: selectContainerRef,
    isEnabled
  })

  // Helper function to check if element intersects with selection area
  const isElementInSelection = useCallback((elementRect, selectionRect) => {
    return !(
      elementRect.y + elementRect.height < selectionRect.y ||
      elementRect.y > selectionRect.y + selectionRect.height ||
      elementRect.x + elementRect.width < selectionRect.x ||
      elementRect.x > selectionRect.x + selectionRect.width
    )
  }, [])

  // Set up IntersectionObserver to track visible panels
  useEffect(() => {
    if (!selectContainerRef.current) {
      return
    }

    // Create observer with container as root
    const observer = new IntersectionObserver(
      (entries) => {
        // This callback fires when panels enter/exit the viewport
        // You can use this to track which panels are visible
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Panel is visible - could mark it for selection eligibility
            entry.target.dataset.visible = 'true'
          } else {
            // Panel is not visible
            entry.target.dataset.visible = 'false'
          }
        })
      },
      {
        root: selectContainerRef.current,
        rootMargin: '0px',
        threshold: 0.1 // Trigger when 10% of panel is visible
      }
    )

    // Observe all panels
    const deckPanels = document.querySelectorAll(`[data-panel-type='DeckPanels']`)
    const sideLiners = document.querySelectorAll(`[data-panel-type='SideLiners']`)
    const allPanels = [...deckPanels, ...sideLiners]

    allPanels.forEach((panel) => observer.observe(panel))

    // Cleanup
    return () => {
      observer.disconnect()
    }
  }, [selectContainerRef])

  useEffect(() => {
    if (!selection || mouseDown) {
      return
    }

    // Query all panels and sideliners once
    const deckPanels = document.querySelectorAll(`[data-panel-type='DeckPanels']`)
    const sideLiners = document.querySelectorAll(`[data-panel-type='SideLiners']`)
    const allPanels = [...deckPanels, ...sideLiners]

    const selected = []
    for (const panel of allPanels) {
      // Optional: Skip panels that aren't visible (optimization)
      // if (panel.dataset.visible === 'false') continue

      const elementPosition = panel.getBoundingClientRect()

      if (isElementInSelection(elementPosition, selection)) {
        try {
          const itemData = JSON.parse(panel.dataset.item)
          selected.push(itemData)
        } catch (error) {
          console.warn('Failed to parse panel data:', error)
        }
      }
    }

    // Update multi-selection flag
    wasMultiSelection.current = selected.length > 0

    onPanelClick(selected)
    setSelection(undefined)
  }, [selection, mouseDown, onPanelClick, setSelection, wasMultiSelection, isElementInSelection])

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      selection,
      setSelection
    }),
    [selection, setSelection]
  )

  return <SelectionContext.Provider value={contextValue}>{children}</SelectionContext.Provider>
}
